import {
  type DragEvent,
  type FunctionComponent,
  type TouchList,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { Button, Emitter, assertClientSide } from '@dr.pogodin/react-utils';

import { useArtist } from 'hooks';

import { type VideoT, isInsideRect, useGlobalState } from 'utils';

import IconArrowLeft from 'assets/images/icon-arrow-left.svg';
import IconCross from 'assets/images/icon-cross-24px.svg';
import IconProfileCircled from 'assets/images/icon-profile-20px.svg';

import greenPlusButtonTheme from 'themes/Button/green-circle-40px.scss';
import transparentIconButtonTheme from 'themes/Button/icon-20px-transparent.scss';
import removeButtonTheme from 'themes/Button/red-circle-48px.scss';
import moreInfoButtonTheme from 'themes/Button/text-white-transparent.scss';

import { Info } from './Info';
import { Practitioner } from './Practitioner';

import S from './style.scss';

const touchCancelEmitter = new Emitter();
const touchDropEmitter = new Emitter<[number, number]>();
const touchMoveEmitter = new Emitter<[number, number]>();

export function addTouchCancelListener(
  listener: () => void,
): () => void {
  assertClientSide();
  return touchCancelEmitter.addListener(listener);
}

export function addTouchDropListener(
  listener: (x: number, y: number) => void,
): () => void {
  assertClientSide();
  return touchDropEmitter.addListener(listener);
}

export function addTouchMoveListener(
  listener: (x: number, y: number) => void,
): () => void {
  assertClientSide();
  return touchMoveEmitter.addListener(listener);
}

let scrollingDirection: 'DOWN' | 'UP' | undefined;
let scrollingTimerId: NodeJS.Timeout | undefined;

function startScrolling(direction: 'DOWN' | 'UP') {
  if (scrollingDirection === direction) return;
  if (scrollingTimerId) clearInterval(scrollingTimerId);

  scrollingDirection = direction;
  scrollingTimerId = setInterval(() => {
    const viewport = window.visualViewport;
    if (viewport) {
      window.scrollBy({
        behavior: 'smooth',
        left: 0,
        top: scrollingDirection === 'UP'
          ? -viewport.height / 4 : viewport.height / 4,
      });
    }
  }, 100);
}

function stopScrolling() {
  if (scrollingTimerId) {
    clearInterval(scrollingTimerId);
    scrollingDirection = undefined;
    scrollingTimerId = undefined;
  }
}

function eventBlocker(e: Event) {
  e.preventDefault();
  e.stopPropagation();
}

function getMidPoint(touches: TouchList): [number, number] {
  const res: [number, number] = [0, 0];

  // It seems, TouchList does not really support for..of.
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < touches.length; ++i) {
    const touch = touches[i];
    if (!touch) throw Error('Internal error');
    res[0] += touch.clientX;
    res[1] += touch.clientY;
  }

  res[0] /= touches.length;
  res[1] /= touches.length;

  return res;
}

type InfoT = 'artist' | 'video';

type HeapT = {
  dragImageElement?: HTMLElement;
  touchOffset?: [number, number];
};

type PropsT = {
  draggable?: boolean;
  droppable?: boolean;
  index?: number;
  onAdd?: (video: VideoT) => void;
  onDragStart?: () => void;
  onDrop?: (e?: DragEvent<HTMLDivElement>) => void;
  onDropZone?: (zone: 'AFTER' | 'BEFORE') => void;
  onRemove?: () => void;
  video: VideoT;
};

export const VideoCard: FunctionComponent<PropsT> = ({
  draggable,
  droppable,
  index,
  onAdd,
  onDragStart,
  onDrop,
  onDropZone,
  onRemove,
  video,
  // eslint-disable-next-line complexity
}) => {
  const bottomScrollAreaRef = useRef<HTMLDivElement>(null);
  const topScrollAreaRef = useRef<HTMLDivElement>(null);
  const [showScrollAreas, setShowScrollAreas] = useState(false);

  const { current: heap } = useRef<HeapT>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const [, setVideoBeingDragged] = useGlobalState('ui.videoBeingDragged');

  const dzEvent = useEffectEvent(onDropZone ?? (() => undefined));
  const dropEvent = useEffectEvent(onDrop ?? (() => undefined));

  const artist = useArtist(video.artistId).data;

  const durationLabel = `${Math.ceil(video.durationMin ?? 0)} Minutes`;
  const authorName = artist
    ? artist.displayName ?? `${artist.user.firstName} ${artist.user.lastName}`
    : undefined;

  let contentClassName = S.container;
  if (draggable) contentClassName += ` ${S.draggable}`;

  /**
   * Initialization of the video card drag, common between the standard Drag &
   * Drop API case, and the custom dragging on a touch surface.
   */
  const initDrag = (): HTMLElement => {
    setVideoBeingDragged(video);

    const container = containerRef.current;
    if (!container) throw Error('Inernal error');

    const res = container.cloneNode(true) as HTMLElement;
    res.style.boxShadow = '0 5px 12.1px 0 #00000040';
    res.style.position = 'fixed';
    res.style.height = `${container.offsetHeight}px`;
    res.style.width = `${container.offsetWidth}px`;
    res.style.top = '100dvh';
    res.style.left = '100dvw';

    heap.dragImageElement = res;
    document.body.appendChild(res);

    onDragStart?.();

    return res;
  };

  /**
   * Cleans up after the drag end.
   */
  const cleanUpDrag = () => {
    setVideoBeingDragged(undefined);

    heap.dragImageElement?.remove();
    delete heap.dragImageElement;

    window.removeEventListener('touchmove', eventBlocker);
    setShowScrollAreas(false);
    stopScrolling();
  };

  const [info, setInfo] = useState<InfoT>();

  let res = (
    <div
      className={contentClassName}
      draggable={draggable}
      onDragEnd={draggable ? cleanUpDrag : undefined}
      onDragStart={draggable ? (e) => {
        const icon = initDrag();
        e.dataTransfer.setDragImage(icon, 30, 30);
      } : undefined}
      onTouchCancel={draggable ? (e) => {
        if (!e.targetTouches.length) {
          touchCancelEmitter.emit();
          cleanUpDrag();
        }
      } : undefined}
      onTouchEnd={draggable ? (e) => {
        if (!e.targetTouches.length) {
          // NOTE: It looks like there are some (probably edge) cases when
          // the drag may be cleaned up by the time we arrive here. Not sure,
          // is it a bug, or legit behavior. For now, let's just correct
          // the code with safeguard to not crash the app.
          const iconRect = heap.dragImageElement?.getBoundingClientRect();
          if (iconRect) {
            if (!heap.touchOffset) throw Error('Missing touch offset data');
            touchDropEmitter.emit(
              iconRect.left + heap.touchOffset[0],
              iconRect.top + heap.touchOffset[1],
            );
          }
          cleanUpDrag();
        }
      } : undefined}
      onTouchMove={draggable ? (e) => {
        const icon = heap.dragImageElement;
        if (!icon) return;

        const offset = heap.touchOffset;
        if (!offset) throw Error('Internal error');

        const point = getMidPoint(e.targetTouches);

        icon.style.top = `${point[1] - offset[1]}px`;
        icon.style.left = `${point[0] - offset[0]}px`;

        touchMoveEmitter.emit(point[0], point[1]);

        const bottomScrollArea = bottomScrollAreaRef.current
          ?.getBoundingClientRect();
        const topScrollArea = topScrollAreaRef.current?.getBoundingClientRect();

        if (isInsideRect(point, bottomScrollArea)) startScrolling('DOWN');
        else if (isInsideRect(point, topScrollArea)) startScrolling('UP');
        else stopScrolling();
      } : undefined}
      onTouchStart={draggable ? (e) => {
        // Don't start a drag when the touch lands on an action button (+, info,
        // remove). Otherwise the drag start/drop cycle eats the first tap and
        // the button needs a second tap to register its click.
        if ((e.target as HTMLElement).closest('button, [role="button"]')) return;

        const icon = initDrag();
        const point = getMidPoint(e.targetTouches);

        // NOTE: It should not be "e.target", because that might be the internal
        // (meta-data) element.
        const target = containerRef.current?.getBoundingClientRect();
        if (target) {
          heap.touchOffset = [point[0] - target.left, point[1] - target.top];

          icon.style.top = `${point[1] - heap.touchOffset[1]}px`;
          icon.style.left = `${point[0] - heap.touchOffset[0]}px`;
        }

        setShowScrollAreas(true);
        window.addEventListener('touchmove', eventBlocker, { passive: false });
      } : undefined}
      ref={containerRef}
      style={{ backgroundImage: `url(${video.thumbnailUrl})` }}
    >
      <div styleName="metaData">
        {video.intent ? <div styleName="browseTags">{video.intent}</div> : null}
        <div styleName="browseTitle">{video.title ?? 'Untitled'}</div>
        <div styleName="browseFooter">
          <div styleName="browseInfo">
            <span>{durationLabel}</span>
            {authorName ? (
              <>
                <span styleName="dot">•</span>
                <span>{authorName}</span>
                <Button
                  onClick={() => {
                    setInfo('artist');
                  }}
                  theme={transparentIconButtonTheme}
                >
                  <IconProfileCircled stroke="white" />
                </Button>
              </>
            ) : null}
          </div>
          <Button
            onClick={() => {
              setInfo('video');
            }}
            theme={moreInfoButtonTheme}
          >
            More info
            <IconArrowLeft styleName="moreInfoIcon" />
          </Button>
        </div>
      </div>
      {video.status === 'Published'
        ? null
        : <div styleName="removed">Removed</div>}
      {index ? <div styleName="index">{index}</div> : null}
      {onAdd ? (
        <div styleName="plusButtonWrapper">
          <Button
            onClick={() => {
              onAdd(video);
            }}
            theme={greenPlusButtonTheme}
          >
            +
          </Button>
        </div>
      ) : null}
      {onRemove ? (
        <div styleName="removeButtonWrapper">
          <Button
            onClick={onRemove}
            theme={removeButtonTheme}
          >
            <IconCross stroke="white" />
          </Button>
        </div>
      ) : null}
      {showScrollAreas ? (
        <>
          <div
            ref={topScrollAreaRef}
            styleName="topScrollArea"
          />
          <div
            ref={bottomScrollAreaRef}
            styleName="bottomScrollArea"
          />
        </>
      ) : null}
      {info === 'video' ? (
        <Info
          onClose={() => {
            setInfo(undefined);
          }}
          video={video}
        />
      ) : null}
      {info === 'artist' ? (
        <Practitioner
          artist={artist}
          onClose={() => {
            setInfo(undefined);
          }}
        />
      ) : null}
    </div>
  );

  const dropZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubA = addTouchMoveListener((x: number, y: number) => {
      const rect = dropZoneRef.current?.getBoundingClientRect();
      if (rect && isInsideRect([x, y], rect)) {
        dzEvent(y < 0.5 * (rect.top + rect.bottom) ? 'BEFORE' : 'AFTER');
      }
    });

    const unsubB = addTouchDropListener((x: number, y: number) => {
      const rect = dropZoneRef.current?.getBoundingClientRect();
      if (isInsideRect([x, y], rect)) {
        dropEvent();
      }
    });

    return () => {
      unsubA();
      unsubB();
    };
  }, []);

  if (droppable) {
    res = (
      <div
        onDragOver={onDropZone ? (e) => {
          e.preventDefault();

          if (!dropZoneRef.current) return;

          const rect = dropZoneRef.current.getBoundingClientRect();
          onDropZone(
            e.clientY < 0.5 * (rect.top + rect.bottom) ? 'BEFORE' : 'AFTER',
          );

          // NOTE: This is important - as during the dragging we add a listener
          // to the document, which clears up the precise drop area if triggered;
          // thus, any components that evaluate the precise drop area are expected
          // to stop the event propagation, to keep the drop zone rendered.
          e.stopPropagation();
        } : undefined}
        onDrop={onDrop}
        ref={dropZoneRef}
        styleName="dropWrapper"
      >
        {res}
      </div>
    );
  }

  return res;
};
